/**
 * RfisTab
 * -------
 * Numbered RFI log with ball-in-court, response deadlines, attachments and a
 * printable transmittal. A cost-bearing answer can be turned into a draft
 * change order in one click.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Plus, Loader2, Download, Send, Trash2, Upload, Paperclip, MessagesSquare,
  AlertTriangle, CheckCircle2, FileSignature, Ban, RotateCcw,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { API } from "@/App";

const fmt$ = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const STATUS_STYLE = {
  draft: "bg-slate-100 text-slate-600 ring-slate-300",
  open: "bg-amber-50 text-amber-700 ring-amber-300",
  answered: "bg-blue-50 text-blue-700 ring-blue-300",
  closed: "bg-emerald-50 text-emerald-700 ring-emerald-300",
  void: "bg-slate-100 text-slate-400 ring-slate-200",
};

const blank = () => ({
  subject: "", question: "", discipline: "", division_code: "", spec_section: "",
  drawing_reference: "", location: "", asked_of: "", responsible_company: "",
  date_required: "", suggested_solution: "",
});

const Stat = ({ label, value, accent = "text-slate-900", sub }) => (
  <div className="rounded-xl border border-slate-200 bg-white p-3.5 shadow-sm">
    <p className="text-[10px] font-black uppercase tracking-[0.14em] text-slate-400">{label}</p>
    <p className={`text-lg font-black mt-1 ${accent}`}>{value}</p>
    {sub ? <p className="text-[11px] text-slate-500 mt-0.5">{sub}</p> : null}
  </div>
);

export default function RfisTab({ projectId, project, token, onContractChange }) {
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [answering, setAnswering] = useState(null);
  const [answer, setAnswer] = useState({ answer: "", cost_impact: false, cost_amount: 0, schedule_impact_days: 0 });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/rfis?project_id=${projectId}`, { headers });
      setRows(r.data?.rfis || []);
      setSummary(r.data?.summary || null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load the RFI log");
    } finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, token]);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!form.subject.trim()) return toast.error("Give the RFI a subject");
    setSaving(true);
    try {
      const r = editingId
        ? await axios.put(`${API}/rfis/${editingId}`, form, { headers })
        : await axios.post(`${API}/rfis`, { project_id: projectId, ...form }, { headers });
      toast.success(`${r.data.rfi.number} saved`);
      setForm(null); setEditingId(null); load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save the RFI");
    } finally { setSaving(false); }
  };

  const act = async (row, action) => {
    try {
      await axios.post(`${API}/rfis/${row.id}/action`, { action }, { headers });
      toast.success(`${row.number} ${action === "submit" ? "issued" : action}d`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || `Could not ${action}`);
    }
  };

  const sendAnswer = async () => {
    if (!answer.answer.trim()) return toast.error("Write the response first");
    try {
      await axios.post(`${API}/rfis/${answering.id}/answer`, {
        ...answer,
        cost_amount: Number(answer.cost_amount || 0),
        schedule_impact_days: Number(answer.schedule_impact_days || 0),
      }, { headers });
      toast.success(`${answering.number} answered`);
      setAnswering(null); load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not record the answer");
    }
  };

  const toChangeOrder = async (row) => {
    try {
      const r = await axios.post(`${API}/rfis/${row.id}/change-order`, {}, { headers });
      toast.success(`${r.data.change_order.number} drafted from ${row.number}`, {
        description: `${fmt$(r.data.change_order.total)} — open the Change Orders tab to price and approve it`,
      });
      load(); onContractChange?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create the change order");
    }
  };

  const upload = async (row, file) => {
    const fd = new FormData();
    fd.append("file", file);
    try {
      await axios.post(`${API}/rfis/${row.id}/attachments`, fd,
        { headers: { ...headers, "Content-Type": "multipart/form-data" } });
      toast.success(`${file.name} attached`); load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    }
  };

  const remove = async (row) => {
    if (!window.confirm(`Delete ${row.number}?`)) return;
    try {
      await axios.delete(`${API}/rfis/${row.id}`, { headers });
      toast.success(`${row.number} deleted`); load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not delete");
    }
  };

  const openPdf = async (row) => {
    try {
      const r = await axios.get(`${API}/rfis/${row.id}/pdf`, { headers, responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = `${row.number}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Could not generate the transmittal");
    }
  };

  if (loading) {
    return <p className="text-slate-500 inline-flex items-center gap-2 py-8">
      <Loader2 className="w-4 h-4 animate-spin" />Loading RFI log…</p>;
  }

  return (
    <div className="space-y-5" data-testid="rfis-tab">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-lg font-bold text-slate-900">RFI Log</h2>
          <p className="text-sm text-slate-500">
            Questions on the contract documents, with response deadlines. Answers that cost money become change orders.
          </p>
        </div>
        <Button size="sm" className="bg-blue-600 hover:bg-blue-700 text-white"
          onClick={() => { setEditingId(null); setForm({ ...blank(), asked_of: project?.pm_name || "" }); }}
          data-testid="new-rfi-btn">
          <Plus className="w-4 h-4 mr-2" />New RFI
        </Button>
      </div>

      {summary && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="rfi-summary">
          <Stat label="Total RFIs" value={summary.total} />
          <Stat label="Awaiting answer" value={summary.ball_with_pm} accent="text-amber-600" />
          <Stat label="Overdue" value={summary.overdue}
            accent={summary.overdue ? "text-red-600" : "text-slate-900"} sub="Past response deadline" />
          <Stat label="Cost-bearing answers"
            value={rows.filter((r) => r.cost_impact).length} accent="text-blue-700"
            sub={`${rows.filter((r) => r.change_order_number).length} became change orders`} />
        </div>
      )}

      {rows.length === 0 ? (
        <div className="text-center py-12 rounded-xl border border-dashed border-slate-300 bg-white"
          data-testid="rfis-empty">
          <MessagesSquare className="w-12 h-12 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-600 font-semibold">No RFIs on this project yet</p>
          <p className="text-sm text-slate-400 mt-1">Field questions get a number, a deadline and a paper trail.</p>
        </div>
      ) : (
        <div className="rounded-xl border border-slate-200 bg-white overflow-x-auto shadow-sm">
          <table className="w-full min-w-[1060px]">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                {["No.", "Subject", "From", "Response due", "Impact", "Ball in court", "Status", ""].map((h) => (
                  <th key={h} className="text-left px-4 py-2.5 text-[10px] font-black uppercase tracking-wider text-slate-500">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map((row) => (
                <tr key={row.id} className="hover:bg-slate-50/70" data-testid={`rfi-row-${row.number}`}>
                  <td className="px-4 py-3 font-mono text-xs font-bold text-blue-700">{row.number}</td>
                  <td className="px-4 py-3">
                    <p className="text-sm font-semibold text-slate-900">{row.subject}</p>
                    <p className="text-xs text-slate-500">
                      {row.discipline || row.division_code || "—"}
                      {row.drawing_reference ? ` · ${row.drawing_reference}` : ""}
                      {(row.attachments || []).length ? ` · ${row.attachments.length} file(s)` : ""}
                    </p>
                  </td>
                  <td className="px-4 py-3 text-sm text-slate-600">{row.responsible_company || row.created_by_name}</td>
                  <td className="px-4 py-3 text-sm">
                    <span className={row.is_overdue ? "text-red-600 font-bold inline-flex items-center gap-1" : "text-slate-600"}>
                      {row.is_overdue ? <AlertTriangle className="w-3.5 h-3.5" /> : null}
                      {row.date_required || "—"}
                    </span>
                    {row.days_open !== null ? (
                      <span className="block text-[11px] text-slate-400">{row.days_open} days open</span>
                    ) : null}
                  </td>
                  <td className="px-4 py-3 text-sm">
                    {row.cost_impact || row.schedule_impact_days ? (
                      <span className="text-slate-700">
                        {row.cost_impact ? fmt$(row.cost_amount) : ""}
                        {row.schedule_impact_days ? ` · +${row.schedule_impact_days}d` : ""}
                        {row.change_order_number ? (
                          <span className="block text-[11px] font-bold text-emerald-600">{row.change_order_number}</span>
                        ) : null}
                      </span>
                    ) : <span className="text-slate-400">—</span>}
                  </td>
                  <td className="px-4 py-3 text-sm text-slate-600">{row.ball_in_court}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wide ring-1 ${STATUS_STYLE[row.status]}`}>
                      {row.status}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      {row.status === "draft" && (
                        <Button size="sm" variant="ghost" className="text-amber-700 hover:bg-amber-50"
                          onClick={() => act(row, "submit")} data-testid={`rfi-submit-${row.number}`}>
                          <Send className="w-3.5 h-3.5 mr-1" />Issue
                        </Button>
                      )}
                      {["open", "answered", "draft"].includes(row.status) && (
                        <Button size="sm" variant="ghost" className="text-blue-700 hover:bg-blue-50"
                          onClick={() => {
                            setAnswering(row);
                            setAnswer({
                              answer: row.answer || "", cost_impact: !!row.cost_impact,
                              cost_amount: row.cost_amount || 0,
                              schedule_impact_days: row.schedule_impact_days || 0,
                            });
                          }}
                          data-testid={`rfi-answer-${row.number}`}>
                          {row.answer ? "Edit answer" : "Answer"}
                        </Button>
                      )}
                      {row.cost_impact && !row.change_order_number && (
                        <Button size="sm" variant="ghost" className="text-emerald-700 hover:bg-emerald-50"
                          onClick={() => toChangeOrder(row)} data-testid={`rfi-to-co-${row.number}`}>
                          <FileSignature className="w-3.5 h-3.5 mr-1" />Change order
                        </Button>
                      )}
                      {row.status === "answered" && (
                        <Button size="sm" variant="ghost" className="text-emerald-700 hover:bg-emerald-50"
                          onClick={() => act(row, "close")} data-testid={`rfi-close-${row.number}`}>
                          <CheckCircle2 className="w-3.5 h-3.5 mr-1" />Close
                        </Button>
                      )}
                      {["closed", "void"].includes(row.status) && (
                        <Button size="sm" variant="ghost" className="text-slate-600 hover:bg-slate-100"
                          onClick={() => act(row, "reopen")}>
                          <RotateCcw className="w-3.5 h-3.5" />
                        </Button>
                      )}
                      {["draft", "open", "answered"].includes(row.status) && (
                        <Button size="sm" variant="ghost" className="text-slate-500 hover:bg-slate-100"
                          onClick={() => act(row, "void")} title="Void">
                          <Ban className="w-3.5 h-3.5" />
                        </Button>
                      )}
                      <label className="inline-flex items-center px-2 py-1 rounded-md text-slate-500 hover:bg-slate-100 cursor-pointer"
                        title="Attach a file" data-testid={`rfi-attach-${row.number}`}>
                        <Upload className="w-3.5 h-3.5" />
                        <input type="file" className="hidden"
                          onChange={(e) => { const f = e.target.files?.[0]; if (f) upload(row, f); e.target.value = ""; }} />
                      </label>
                      {(row.attachments || []).length > 0 && (
                        <a href={`${process.env.REACT_APP_BACKEND_URL}${row.attachments[row.attachments.length - 1].url}`}
                          target="_blank" rel="noreferrer"
                          className="px-2 py-1 text-slate-500 hover:text-slate-800" title="Open latest attachment">
                          <Paperclip className="w-3.5 h-3.5" />
                        </a>
                      )}
                      <Button size="sm" variant="ghost" className="text-blue-700 hover:bg-blue-50"
                        onClick={() => openPdf(row)} data-testid={`rfi-pdf-${row.number}`}>
                        <Download className="w-3.5 h-3.5" />
                      </Button>
                      <Button size="sm" variant="ghost" className="text-red-600 hover:bg-red-50"
                        onClick={() => remove(row)}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={!!form} onOpenChange={(o) => !o && setForm(null)}>
        <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingId ? "Edit RFI" : "New RFI"}</DialogTitle>
            <DialogDescription>Ask the question in writing, with the drawing reference and the date you need it back.</DialogDescription>
          </DialogHeader>
          {form && (
            <div className="grid sm:grid-cols-2 gap-3">
              <div className="sm:col-span-2">
                <label className="text-[10px] font-black uppercase tracking-wider text-slate-500">Subject</label>
                <Input value={form.subject} placeholder="Conflict between medical gas rough-in and ceiling grid at Room 4"
                  onChange={(e) => setForm({ ...form, subject: e.target.value })} data-testid="rfi-subject-input" />
              </div>
              <div className="sm:col-span-2">
                <label className="text-[10px] font-black uppercase tracking-wider text-slate-500">Question</label>
                <Textarea rows={4} value={form.question}
                  onChange={(e) => setForm({ ...form, question: e.target.value })} data-testid="rfi-question-input" />
              </div>
              <div>
                <label className="text-[10px] font-black uppercase tracking-wider text-slate-500">Discipline</label>
                <Input value={form.discipline} placeholder="Mechanical"
                  onChange={(e) => setForm({ ...form, discipline: e.target.value })} />
              </div>
              <div>
                <label className="text-[10px] font-black uppercase tracking-wider text-slate-500">Division</label>
                <Input value={form.division_code} placeholder="DIV 22 · Plumbing & Medical Gas"
                  onChange={(e) => setForm({ ...form, division_code: e.target.value })} />
              </div>
              <div>
                <label className="text-[10px] font-black uppercase tracking-wider text-slate-500">Spec section</label>
                <Input value={form.spec_section}
                  onChange={(e) => setForm({ ...form, spec_section: e.target.value })} />
              </div>
              <div>
                <label className="text-[10px] font-black uppercase tracking-wider text-slate-500">Drawing reference</label>
                <Input value={form.drawing_reference} placeholder="A-201 / M-103"
                  onChange={(e) => setForm({ ...form, drawing_reference: e.target.value })} />
              </div>
              <div>
                <label className="text-[10px] font-black uppercase tracking-wider text-slate-500">Location</label>
                <Input value={form.location} placeholder="Isolation Room 4"
                  onChange={(e) => setForm({ ...form, location: e.target.value })} />
              </div>
              <div>
                <label className="text-[10px] font-black uppercase tracking-wider text-slate-500">Asked of</label>
                <Input value={form.asked_of} placeholder="Architect of record"
                  onChange={(e) => setForm({ ...form, asked_of: e.target.value })} />
              </div>
              <div>
                <label className="text-[10px] font-black uppercase tracking-wider text-slate-500">Raised by (company)</label>
                <Input value={form.responsible_company}
                  onChange={(e) => setForm({ ...form, responsible_company: e.target.value })} />
              </div>
              <div>
                <label className="text-[10px] font-black uppercase tracking-wider text-slate-500">Response needed by</label>
                <Input type="date" value={form.date_required || ""}
                  onChange={(e) => setForm({ ...form, date_required: e.target.value })}
                  data-testid="rfi-due-input" />
              </div>
              <div className="sm:col-span-2">
                <label className="text-[10px] font-black uppercase tracking-wider text-slate-500">Suggested solution</label>
                <Textarea rows={2} value={form.suggested_solution}
                  onChange={(e) => setForm({ ...form, suggested_solution: e.target.value })} />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setForm(null)}>Cancel</Button>
            <Button className="bg-blue-600 hover:bg-blue-700 text-white" onClick={save} disabled={saving}
              data-testid="rfi-save-btn">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}Save RFI
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!answering} onOpenChange={(o) => !o && setAnswering(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Answer {answering?.number}</DialogTitle>
            <DialogDescription>{answering?.subject}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
              <p className="text-[10px] font-black uppercase tracking-wider text-slate-500 mb-1">Question</p>
              <p className="text-sm text-slate-700 whitespace-pre-wrap">{answering?.question || "—"}</p>
            </div>
            <div>
              <label className="text-[10px] font-black uppercase tracking-wider text-slate-500">Response</label>
              <Textarea rows={5} value={answer.answer}
                onChange={(e) => setAnswer({ ...answer, answer: e.target.value })}
                data-testid="rfi-answer-input" />
            </div>
            <div className="flex items-center gap-3">
              <Switch checked={answer.cost_impact} data-testid="rfi-cost-impact-switch"
                onCheckedChange={(v) => setAnswer({ ...answer, cost_impact: v })} />
              <span className="text-sm text-slate-700">This answer changes the cost of the work</span>
            </div>
            {answer.cost_impact && (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] font-black uppercase tracking-wider text-slate-500">Estimated amount</label>
                  <Input type="number" value={answer.cost_amount}
                    onChange={(e) => setAnswer({ ...answer, cost_amount: e.target.value })}
                    data-testid="rfi-cost-amount-input" />
                </div>
                <div>
                  <label className="text-[10px] font-black uppercase tracking-wider text-slate-500">Schedule days</label>
                  <Input type="number" value={answer.schedule_impact_days}
                    onChange={(e) => setAnswer({ ...answer, schedule_impact_days: e.target.value })} />
                </div>
              </div>
            )}
            {answer.cost_impact && (
              <p className="text-xs text-slate-500">
                After saving, use <b>Change order</b> on the row to draft a priced change order from this answer.
              </p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAnswering(null)}>Cancel</Button>
            <Button className="bg-blue-600 hover:bg-blue-700 text-white" onClick={sendAnswer}
              data-testid="rfi-answer-save-btn">Record answer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
